import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import "rc-slider/assets/index.css";
import "./styles/index.scss";
import "./index.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Toaster, toast, useToasterStore } from "react-hot-toast";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { DataProvider } from "data/data-provider";
import serverURL from "api/auth/auth";
import { getCompanyID } from "api/company/company";
import { setPostHogCompany } from "analytics/posthog";
import { setStripeAvailability } from "utils/stripeAvailability";

// Function to fetch the Stripe key from the backend
async function fetchStripeKey(
  companyId: number
): Promise<{ publishableKey: string; stripeAccount?: string } | null> {
  try {
    const response = await fetch(
      `${serverURL}/api/payment/publishable-key/?company_id=${companyId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const contentType = response.headers.get("content-type");
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to fetch Stripe key. Status:", response.status);
      console.error("Response Error Body:", errorText);
      return null;
    }

    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();

      if (data.publishable_key) {
        // v2 (API-created) hosts return the PLATFORM publishable key plus the
        // connected account id; Stripe.js must be initialised with
        // `stripeAccount` so PaymentIntents created on that account can be
        // confirmed. Legacy OAuth hosts omit stripe_account. (SU1 / 1NOW-1234)
        return {
          publishableKey: data.publishable_key,
          stripeAccount: data.stripe_account || undefined,
        };
      } else {
        console.error("publishable_key is undefined in the API response.");
        return null;
      }
    } else {
      const errorText = await response.text();
      console.error("Unexpected content type:", contentType);
      console.error("Response Error Body:", errorText);
      return null;
    }
  } catch (error) {
    console.error("Error fetching Stripe key:", error);
    return null;
  }
}

/**
 * One failure on screen at a time.
 *
 * A single renter action fans out into several requests — a search hits
 * locations, availability and pricing together — so one dropped connection
 * stacked three identical toasts down the corner of the page. That reads as
 * three separate things having gone wrong, and buries whatever the renter was
 * looking at underneath them.
 *
 * Giving every error the same toast id makes react-hot-toast update the
 * existing one in place instead of mounting another, so the slot is reused
 * with no stacking and no cross-fade between two boxes. Done here, once, on
 * the shared `toast` singleton rather than at 250-odd call sites; a caller
 * that passes its own id still wins, which is why the store-based cap below
 * stays as a backstop.
 */
const ERROR_TOAST_ID = "renter-error";
const showError = toast.error;
toast.error = ((message: any, options?: any) =>
  showError(message, {
    id: ERROR_TOAST_ID,
    // Set per-toast, not on <Toaster toastOptions>. react-hot-toast does not
    // merge ariaProps from there, so configuring it on the Toaster silently
    // left every error on the library default of role="status" /
    // aria-live="polite" — verified in the DOM. Polite means a screen reader
    // waits for an idle moment, so a declined card could be announced late or
    // not at all. Failures should interrupt.
    ariaProps: { role: "alert", "aria-live": "assertive" as const },
    ...options,
  })) as typeof toast.error;

/**
 * Backstop for error toasts raised with an explicit id of their own, which
 * bypass the shared slot above. Dismissing the surplus from the store is
 * react-hot-toast's own documented pattern for capping concurrent toasts; the
 * store keeps the newest at index 0, so this keeps the error matching whatever
 * the renter just did. Success and loading toasts are untouched.
 */
const SingleErrorToast = () => {
  const { toasts } = useToasterStore();

  useEffect(() => {
    toasts
      .filter((t) => t.visible && t.type === "error")
      .slice(1)
      .forEach((t) => toast.dismiss(t.id));
  }, [toasts]);

  return null;
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const RootComponent = () => {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeStripe = async () => {
      const { company_id } = await getCompanyID();
      // Scope all PostHog events to this tenant. Runs before the app's routes
      // render (the spinner shows until setLoading(false) below), so the
      // company_id super-property is set before the first $pageview fires.
      setPostHogCompany(company_id);
      // Analytics / ads / vendor scripts are NOT wired here. They're per-tenant
      // config on CompanyWebsiteDetail, injected by the data-provider once the
      // website details land (see analytics/marketingTags). This used to be a
      // hardcoded `if (company_id === …)` chain for five companies — every new
      // tenant meant another branch nobody remembered to add (1NOW-1556).

      if (company_id) {
        const stripeInfo = await fetchStripeKey(company_id);
        if (stripeInfo?.publishableKey) {
          // For v2 hosts, init Stripe.js bound to the connected account so the
          // platform key can confirm PaymentIntents created on it. (1NOW-1234)
          //
          // Swallow the rejection here rather than handing a rejected promise to
          // `<Elements>` (which has no catch of its own). Stripe.js throws for a
          // key it won't accept — "You should not use a restricted key with
          // Stripe.js" for an `rk_…` key — and without this the card fields just
          // render empty and unclickable with nothing explaining why. (1NOW-1608)
          const stripeLoad = loadStripe(
            stripeInfo.publishableKey,
            stripeInfo.stripeAccount
              ? { stripeAccount: stripeInfo.stripeAccount }
              : undefined
          ).catch(error => {
            console.error("Stripe.js failed to initialize:", error);
            return null;
          });
          setStripePromise(stripeLoad);
          setStripeAvailability(stripeLoad.then(stripe => !!stripe));
        } else {
          // No usable key (no active Stripe account, or the key endpoint
          // rejected what the host saved). Leave Stripe uninitialised —
          // `loadStripe("")` used to be passed here, which throws inside
          // Stripe.js and lands in the same dead-card-fields state.
          setStripePromise(null);
          setStripeAvailability(Promise.resolve(false));
        }
      } else {
        console.error("Company ID is not available.");
        setStripePromise(null);
        setStripeAvailability(Promise.resolve(false));
        toast.error("Company ID is missing. Unable to initialize Stripe.");
      }

      setLoading(false);
    };

    initializeStripe();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center col-span-full min-h-[100vh]">
        <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-secondary-100"></div>
      </div>
    );
  }

  // Move BrowserRouter to the top-level render, not inside RootComponent
  function DataProviderWithNavigate({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const navigate = useNavigate();
    return <DataProvider navigate={navigate}>{children}</DataProvider>;
  }

  return (
    <GoogleOAuthProvider clientId="792199944204-3u7t512ta8hbrhptdjomhqp2nb7jh41h.apps.googleusercontent.com">
      <DataProviderWithNavigate>
        <Elements stripe={stripePromise}>
          <App />
        </Elements>
        <SingleErrorToast />
        <Toaster
          toastOptions={{
            // Long Stripe / API error strings used to spill outside the
            // toast box (ONENOW-465). Allow the toast to wrap and grow
            // vertically rather than overflowing horizontally.
            style: {
              maxWidth: "min(420px, calc(100vw - 32px))",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            },
            error: {
              // Failures now read as failures at a glance rather than
              // matching the success toast pixel for pixel (see
              // .renter-error-toast). The longer duration stays: error copy
              // now carries a next step, which takes longer to read than
              // "Saved".
              duration: 6000,
              className: "renter-error-toast",
              iconTheme: { primary: "#be123c", secondary: "#fff1f2" },
              // ariaProps deliberately not set here — it is not merged from
              // toastOptions and silently does nothing. It lives on the
              // toast.error wrapper above instead.
            },
          }}
        />
      </DataProviderWithNavigate>
    </GoogleOAuthProvider>
  );
};

// Wrap your app with BrowserRouter at the top-level
root.render(
  <BrowserRouter>
    <RootComponent />
  </BrowserRouter>
);

reportWebVitals();
